import MUIStack from "@mui/material/Stack";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import MUIChip from "@mui/material/Chip";
import Location from "../../Utility/Crud/Location";
import User from "../../Utility/Crud/User";
import InfoItem from "../InfoItem";
import ReportSetting from "../ReportSetting";
import MainContext from "../../Context/MainContext/MainContext";

export default class ReportSettingUserByLocation extends ReportSetting {
  static contextType = MainContext; // For legacy class components

  /**
   * Renders location dropdown for selecting multiple locations.
   */
  renderLocationDropdown() {
    const handleLocationChange = (e, locations) => {
      this.handleSettingChange(
        "locationIds",
        locations.map((location) => location.id),
      );
    };

    return (
      <MUIAutocomplete
        multiple
        options={Location.sort(Object.values(this.context.locationsIndexed))}
        value={Location.read(this.context.locationsIndexed, [
          {
            key: "id",
            value: this.state.settings.locationIds || [],
          },
        ])}
        getOptionLabel={(option) => Location.getDisplayName(option)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={handleLocationChange}
        sx={{ width: "300px" }}
        renderInput={(params) => {
          return (
            <MUITextField
              {...params}
              size="small"
              label="Location"
              variant="standard"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {!this.state.settings.locationIds ||
                    this.state.settings.locationIds.length === 0 ? (
                      <MUIChip
                        label="All Locations"
                        sx={{ margin: "3px" }}
                        color="primary"
                      />
                    ) : null}
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
      />
    );
  }

  /**
   * Renders user dropdown for selecting multiple users.
   */
  renderUserDropdown() {
    const handleUserChange = (e, userprofiles) => {
      this.handleSettingChange(
        "userprofileIds",
        userprofiles.map((userprofile) => userprofile.id),
      );
    };

    let users;
    if (this.state.settings.locationIds?.length > 0) {
      // Limit user dropdown to users at the specified location(s).
      users = User.sort(
        User.read(this.context.usersIndexed, [
          {
            key: "active",
            value: !this.props.showInactiveUsers ?? true,
          },
          {
            key: "id",
            value: Object.values(this.context.userprofileLocationsIndexed)
              .filter((userprofileLocation) => {
                return this.state.settings.locationIds.includes(
                  userprofileLocation.locationsId,
                );
              })
              .map((userProfileLocation) => {
                return userProfileLocation.userprofileId;
              }),
          },
        ]),
      ).filter((user) => !user.username.includes("api-"));
    } else {
      // Only limit the user dropdown to active users.
      users = User.sort(
        User.read(this.context.usersIndexed, [
          {
            key: "active",
            value: !this.props.showInactiveUsers ?? true,
          },
        ]),
      ).filter((user) => !user.username.includes("api-"));
    }

    return (
      <MUIAutocomplete
        multiple
        options={users}
        getOptionLabel={(user) => User.getDisplayName(user)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, user) => (
          <li {...props} key={user.id} id={user.id} data-user-id={user.id}>
            {User.getDisplayName(user)}
          </li>
        )}
        value={User.read(this.context.usersIndexed, [
          {
            key: "id",
            value: this.state.settings.userprofileIds || [],
          },
        ])}
        onChange={handleUserChange}
        sx={{ width: "300px" }}
        renderInput={(params) => (
          <MUITextField
            {...params}
            size="small"
            label="User"
            variant="standard"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {!this.state.settings.userprofileIds ||
                  this.state.settings.userprofileIds.length === 0 ? (
                    <MUIChip
                      label={
                        this.state.settings.locationIds &&
                        this.state.settings.locationIds.length !== 0
                          ? "All Location Users"
                          : "All Users"
                      }
                      sx={{ margin: "3px" }}
                      color="primary"
                    />
                  ) : null}
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            InputLabelProps={{ shrink: true }}
          />
        )}
      />
    );
  }

  renderContent() {
    return (
      <MUIStack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
        }}
      >
        {this.renderLocationDropdown()}
        {this.renderUserDropdown()}
      </MUIStack>
    );
  }

  renderReadOnly() {
    const locations =
      this.state.settings.locationIds?.length > 0
        ? Location.read(this.context.locationsIndexed, [
            { key: "id", value: this.state.settings.locationIds },
          ])
        : [];

    const users =
      this.state.settings.userprofileIds?.length > 0
        ? User.read(this.context.usersIndexed, [
            { key: "id", value: this.state.settings.userprofileIds },
          ])
        : [];

    const locationValue =
      locations.length === 0
        ? "All Locations"
        : locations.map((loc) => Location.getDisplayName(loc)).join(", ");

    const userValue =
      users.length === 0
        ? this.state.settings.locationIds?.length > 0
          ? "All Location Users"
          : "All Users"
        : users.map((user) => User.getDisplayName(user)).join(", ");

    return (
      <>
        <InfoItem label="Location" value={locationValue} />
        <InfoItem label="User" value={userValue} />
      </>
    );
  }
}
